// src/components/resources/viewer/BaseResourceViewer.jsx

import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Collapse,
  Paper,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../context/AuthContext';
import ConditionalTooltip from '../../common/ConditionalTooltip';

const DebugButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  zIndex: 5,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DebugPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  maxHeight: '300px',
  overflow: 'auto',
}));

const JsonView = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: 0,
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const BaseResourceViewer = ({
  resource,
  title,
  children,
  showDebugButton = true,
  debugPanelMaxHeight = '300px',
  onNavigateBack,
  backButtonText = 'Back',
  ...props
}) => {
  const [showDebug, setShowDebug] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const toggleDebug = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);

  const formattedResource = useMemo(() => {
    try {
      return JSON.stringify(resource || {}, null, 2);
    } catch (e) {
      console.error("Error stringifying resource for debug view:", e);
      return "Error: Could not stringify resource data.";
    }
  }, [resource]);

  return (
    <Box sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1, // THIS IS THE KEY FIX: Make the whole viewer grow
      overflow: 'hidden', // Prevent this container from creating scrollbars
    }}>
      {onNavigateBack &&
        <Button startIcon={<ArrowBackIcon />} onClick={onNavigateBack} sx={{ mb: 2, alignSelf: 'flex-start', flexShrink: 0 }}>
          {backButtonText}
        </Button>}

      {showDebugButton && isAdmin && (
        <ConditionalTooltip title="Toggle Debug View">
          <DebugButton
            size="small"
            color={showDebug ? "primary" : "default"}
            onClick={toggleDebug}
            aria-label="toggle debug view"
          >
            <BugReportIcon fontSize="small" />
          </DebugButton>
        </ConditionalTooltip>
      )}

      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto', // Allow this inner box to scroll if content overflows
      }}>
        {children}

        <Collapse in={showDebug} timeout="auto">
          <DebugPanel
            variant="outlined"
            sx={{
              maxHeight: debugPanelMaxHeight,
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
                Raw Resource Data
              </Typography>
              <Button
                size="small"
                onClick={() => console.log('Resource:', resource)}
                startIcon={<CodeIcon fontSize="small" />}
              >
                Log to Console
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <JsonView>{formattedResource}</JsonView>
          </DebugPanel>
        </Collapse>
      </Box>
    </Box>
  );
};

BaseResourceViewer.propTypes = {
  resource: PropTypes.object,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  showDebugButton: PropTypes.bool,
  debugPanelMaxHeight: PropTypes.string,
  onNavigateBack: PropTypes.func,
  backButtonText: PropTypes.string,
};

BaseResourceViewer.defaultProps = {
  resource: {},
};

export default BaseResourceViewer;